import { useState } from "react";
import Modal from "react-modal";
import { useParams } from "react-router-dom";
import api from "@/components/customAxios/Axios";
import { useLoader } from "@/context/loaderContext";
import { useToast } from "../toast";

Modal.setAppElement("#root");
const customStyles = {
	overlay: {
		backgroundColor: "rgba(0, 0, 0, 0)", // fully transparent; change to rgba(0,0,0,0.5) for dim without blur
	},
	content: {
		top: "50%",
		left: "50%",
		right: "auto",
		bottom: "auto",
		marginRight: "-50%",
		transform: "translate(-50%, -50%)",
		padding: "2rem",
		borderRadius: "10px",
		maxWidth: "700px",
		width: "80%",
		maxHeight: "650px",
		zIndex: 1000,
	},
};

interface CodingQuestion {
	question: string;
	sample_input: string;
	sample_output: string;
}
const UpdateCodingQuestionModal = ({
	currentQuestionIndex,
	question,
	sampleInput,
	sampleOutput,
	codingQuestionSet,
	getQuestions,
}: {
	currentQuestionIndex: number;
	codingQuestionSet: CodingQuestion[];
	question: string;
	sampleInput: string;
	sampleOutput: string;
	getQuestions: () => void;
}) => {
	const toast = useToast();
	const { setLoading } = useLoader();
	const [modalIsOpen, setIsOpen] = useState(false);
	const { id } = useParams();
	const [newQuestion, setNewQuestion] = useState(question);
	const [newSampleInput, setNewSampleInput] = useState(sampleInput);
	const [newSampleOutput, setNewSampleOutput] = useState(sampleOutput);

	function openModal() {
		setIsOpen(true);
	}

	function closeModal() {
		setIsOpen(false);
	}

	const updateQuestion = async () => {
		codingQuestionSet[currentQuestionIndex].question = newQuestion;
		codingQuestionSet[currentQuestionIndex].sample_input = newSampleInput;
		codingQuestionSet[currentQuestionIndex].sample_output = newSampleOutput;

		try {
			setLoading(true);
			const response = await api.post("job_description_question_management", {
				data: { coding_question: [...codingQuestionSet] },
				job_id: id,
				section: "coding",
			});
			if (response.status === 200) {
				toast.success({
					type: "background",
					duration: 5000,
					status: "success",
					title: "Question Updated Successfully",
					description: "The coding question has been updated successfully.",
					open: true,
				});
				getQuestions();
				closeModal();
			}
		} catch (error: any) {
			toast.error({
				type: "background",
				duration: 5000,
				status: "error",
				title: "Error Updating Question",
				description: error.response?.data?.error || error.response?.data?.message || "Failed to update the coding question. Please try again.",
				open: true,
			});
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="">
			<div className="relative group">
				<button
					onClick={openModal}
					className="text-lg font-heading font-semibold hover:scale-110 transition"
				>
					<svg
						width="17"
						height="17"
						viewBox="0 0 17 17"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M15.7185 15.0356H1.28091C0.98487 15.0356 0.734375 15.2861 0.734375 15.5822C0.734375 15.8782 0.98487 16.1287 1.28091 16.1287H15.7185C16.0146 16.1287 16.2651 15.8782 16.2651 15.5822C16.2651 15.2861 16.0146 15.0356 15.7185 15.0356ZM0.757147 10.2535L0.734375 12.8267C0.734375 12.9634 0.77992 13.1228 0.893781 13.2139C1.00764 13.305 1.14428 13.3733 1.28091 13.3733L3.85418 13.3505C3.99081 13.3505 4.12744 13.2822 4.24131 13.1911L13.0769 4.35545C13.2819 4.1505 13.2819 3.78614 13.0769 3.58119L10.5265 1.0307C10.3215 0.825746 9.95715 0.825746 9.7522 1.0307L7.97596 2.80693L0.916553 9.86634C0.802692 9.9802 0.757147 10.1168 0.757147 10.2535ZM11.9156 3.96832L10.9136 4.9703L9.13735 3.19406L10.1393 2.19208L11.9156 3.96832ZM1.85022 10.4812L8.36309 3.96832L10.1393 5.74456L3.62645 12.2574H1.85022V10.4812Z"
							fill="black"
						/>
					</svg>
				</button>
				<p className="absolute w-max text-center right-2 top-6 px-2 py-1 text-sm bg-black text-white rounded -z-10 group-hover:z-50 transition duration-300 ease-in-out">
					Edit Question
				</p>
			</div>
			<Modal
				isOpen={modalIsOpen}
				onRequestClose={closeModal}
				style={customStyles}
				contentLabel="Example Modal"
			>
				{/* Modal Title */}
				<h2 className="font-heading text-xl font-semibold text-gray-800 text-center">
					Edit Question
				</h2>

				{/* Question Input */}
				<div className="mt-4">
					<label className="block text-gray-600 font-medium mb-1">
						Question:
					</label>
					<textarea
						rows={5}
						className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-400 outline-none"
						value={newQuestion}
						onChange={(e) => setNewQuestion(e.target.value)}
					/>
				</div>

				{/* Options */}
				<div className="mt-4">
					<p className="text-gray-600 font-medium mb-2">sample Input:</p>

					<input
						type="text"
						className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-400 outline-none"
						value={newSampleInput}
						onChange={(e) => setNewSampleInput(e.target.value)}
					/>
				</div>
				<div className="mt-4">
					<p className="text-gray-600 font-medium mb-2">sample Output:</p>

					<input
						type="text"
						className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-400 outline-none"
						value={newSampleOutput}
						onChange={(e) => setNewSampleOutput(e.target.value)}
					/>
				</div>

				{/* Update Button */}
				<div className="mt-6 flex justify-end space-x-3">
					<button
						onClick={updateQuestion}
						className="bg-[#BDE4C3] text-[#438437] px-4 py-2 rounded"
					>
						Update
					</button>
					<button
						onClick={closeModal}
						className="px-4 py-2 text-[#438437] border-1 rounded-md hover:bg-gray-300 transition"
					>
						Cancel
					</button>
				</div>
			</Modal>
		</div>
	);
};

export default UpdateCodingQuestionModal;
